/**
 * Reading a proposal off a tool part, and turning it into what the app's own
 * hooks take.
 *
 * Decision 2 of docs/plans/ai-setup-and-agent.md: the agent writes nothing.
 * A write tool answers a proposal, the phone draws a card, and the tap calls
 * the hook the matching screen would call. Three of those hooks do not take
 * the proposal's payload directly, so the translation lives here, pure and
 * tested:
 *
 *   card        the tile vocabulary -> an `InsightLayoutEntry` on the record's
 *               own summary, or a `{ host, record }` entry on the Dashboard,
 *               through the same `parseSummaryTile` a template's tile goes
 *               through
 *   structure   the PATCH body -> the edits the customize draft store takes,
 *               so `useStructure().save()` sends what the screens would send
 *   link        the POST body -> the `LinkDraft` the link editor holds
 *
 * Every read off a part is defensive. A tool output crosses the wire as JSON
 * the model shaped; the server validated it, but a cast is still not a read.
 */
import type {
  AgentProposal,
  AskCreateRequest,
  PermissionKey,
  WideAgentProposal,
  CardProposal,
  FieldSpec,
  FormulaSpec,
  FormulaWordLookup,
  InviteProposal,
  LinkPatchRequest,
  LinkProposal,
  RecordIconId,
  RecordLayout,
  RecordStructureRequest,
  RecordSchemaResponse,
  RefusedOutput,
  StructureFieldAdd,
  StructureFieldPatch,
  StructureProposal,
} from '@alluzo/shared';
import {
  fillPendingLink,
  formulaFieldsRead,
  formulaWithFieldIds,
  isPendingLink,
  linesOf,
  PERMISSION_KEYS,
  readFormulaSpec,
  readLedgerCardSpec,
  ROLE_PROPOSAL_TYPES,
  VALUE_TYPES,
  withPendingRecordId,
} from '@alluzo/shared';
import type { UIMessage } from 'ai';

import { t } from '@/lib/i18n';
import {
  businessEntry,
  drawsLedgerTiles,
  type BusinessInsightLayoutEntry,
} from '@/lib/insight-business-layout';
import { isLeaderSpec, layoutEntry, parseSummaryTile, type InsightLayoutEntry } from '@/lib/insight-layout';
import type { LinkDraft } from '@/lib/link-draft';
import type { DraftField, DraftOption, useStructureDraft } from '@/stores/structure-draft';

type Part = UIMessage['parts'][number];

type ToolPartLike = {
  type: string;
  toolCallId?: string;
  state?: string;
  output?: unknown;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The tool output a part carries, once it has landed. Null for a text part or a call still arriving. */
const landedOutput = (part: Part): Record<string, unknown> | null => {
  if (!part.type.startsWith('tool-')) return null;
  const held = part as Part & ToolPartLike;
  if (held.state !== 'output-available' || !isObject(held.output)) return null;
  return held.output;
};

const PROPOSAL_TYPES: ReadonlySet<string> = new Set([
  'entry',
  'entries',
  'entry_change',
  'structure',
  'link',
  'link_change',
  'card',
  'card_remove',
  'record_remove',
  'field_convert',
  'invite',
  'invites',
  'bundle',
  'record',
  'job',
  'job_change',
  'job_apply',
  'grant',
  'business',
  'fact',
  'member_change',
  'primary_request',
]);

/** The kinds sent only to a client that says it draws roles (web-redesign-04 phase 6). */
const ROLE_PROPOSAL_KINDS: ReadonlySet<string> = new Set(ROLE_PROPOSAL_TYPES);

/**
 * The proposal a tool part carries, or null.
 *
 * A `setup` proposal is not one of these: it belongs to the setup door, which
 * reads it with `setupProposalFrom` and confirms it through compose.
 */
export const proposalOf = (part: Part): AgentProposal | null => {
  const proposal = wideProposalOf(part);
  return proposal === null || ROLE_PROPOSAL_KINDS.has(proposal.type) ? null : (proposal as AgentProposal);
};

/**
 * The same, with the role and access kinds as well: what the phone draws,
 * now that it sends the `roles` feature. `proposalOf` keeps answering only
 * the kinds every client draws, for a reader that does not draw roles yet.
 */
export const wideProposalOf = (part: Part): WideAgentProposal | null => {
  const output = landedOutput(part);
  if (output === null || output.kind !== 'proposal' || !isObject(output.proposal)) return null;
  const { type } = output.proposal;
  if (typeof type !== 'string' || !(PROPOSAL_TYPES.has(type) || ROLE_PROPOSAL_KINDS.has(type))) return null;
  return output.proposal as unknown as WideAgentProposal;
};

/**
 * The tool call of every card a turn's messages draw, in order: each part
 * `wideProposalOf` reads, as the thread draws a card for it, by the id the card
 * registers under. A call notes these as drawn in it (rule 4a, A26).
 */
export const proposalCallIdsOf = (messages: readonly UIMessage[]): string[] =>
  messages.flatMap((message) =>
    message.parts.flatMap((part) => {
      const { toolCallId } = part as Part & ToolPartLike;
      return typeof toolCallId === 'string' && wideProposalOf(part) !== null ? [toolCallId] : [];
    }),
  );

/** What the card's Edit needs found before it can open its form. */
export type EditDoorFound = {
  /** A link change: the link it changes is in the record's links. */
  readonly link: boolean;
  /** An insight card: its draft could be laid out from the record's schema. */
  readonly insight: boolean;
};

/**
 * Whether a card draws its Edit, the phone's "Change it first" (Decided 9): only where Edit opens a form with
 * the proposal in it. A fact's words are the whole card; a new list, a bundle
 * and a setup have no form to change them in first; a link change or an
 * insight card waits until what it changes is found. An Edit that opens
 * nothing would still tell a call the card was left. `useAgentConfirm` on
 * both clients answers it as `canEditFirst`, with what it has found.
 */
export const hasEditDoor = (type: WideAgentProposal['type'], found: EditDoorFound): boolean => {
  switch (type) {
    case 'fact':
    case 'record':
    case 'record_remove':
    case 'field_convert':
    case 'bundle':
    case 'kit':
    case 'setup':
    // The Link editor is gone with its routes (step 9.5 of docs/plans/ledger.md): no door to open.
    case 'link':
    case 'link_change':
      return false;
    case 'card':
      return found.insight;
    default:
      return true;
  }
};

/**
 * A tool's refusal, or null. Every write tool refuses in this one shape.
 *
 * A refused `hand_to` is null: its sentence ("You may not switch this turn to
 * the team specialist.") is for the model, which carries on in its own seat.
 * To the person Coach is one assistant (FD7), so no card names a seat, and the
 * working row stays while the turn goes on.
 */
export const refusalOf = (part: Part): RefusedOutput | null => {
  if (part.type === 'tool-hand_to') return null;
  const output = landedOutput(part);
  if (output === null || output.kind !== 'refused') return null;
  if (typeof output.code !== 'string' || typeof output.message !== 'string') return null;
  const problems = Array.isArray(output.problems)
    ? output.problems.filter(
        (one): one is RefusedOutput['problems'][number] =>
          isObject(one) && typeof one.code === 'string' && typeof one.message === 'string',
      )
    : [];
  const message = forThePerson(output.message);
  const kept = problems.flatMap((one) => {
    const words = forThePerson(one.message);
    return words === '' ? [] : [{ ...one, message: words }];
  });
  const refusal: RefusedOutput = { kind: 'refused', code: output.code, message, problems: kept };
  // A refusal that was only ever the model's own miss (an id it did not have,
  // a read it should make first) says nothing to the person: the model's
  // reply says it in plain words.
  if (message === '' && kept.length === 0 && !isFence(refusal)) return null;
  return refusal;
};

/**
 * A refusal the same tool put right later in the same message: the model's
 * own earlier try ("Date is required"), which a proposal from that tool then
 * replaced. Only the proposal is for the person; the stale refusal above it
 * would read as if the final card were refused too.
 */
export const isSupersededRefusal = (parts: readonly Part[], index: number): boolean => {
  const part = parts[index];
  if (part === undefined || refusalOf(part) === null) return false;
  return parts.slice(index + 1).some((later) => later.type === part.type && wideProposalOf(later) !== null);
};

/**
 * A tool's refusal is written for the model, and some of its sentences are
 * only for the model: "Read list_entries for the id." The house rule is that
 * the person never sees a tool's name or an id, so a sentence naming either
 * is dropped. A quoted word is the person's own and never counts.
 */
const forThePerson = (message: string): string =>
  message
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => {
      const bare = sentence.replace(/"[^"]*"|“[^”]*”/g, '');
      return !/\b[a-z]+(?:_[a-z]+)+\b/.test(bare) && !/\bids?\b/i.test(bare);
    })
    .join(' ')
    .trim();

/** Whether a refusal card has anything under its banner: the fence's sentence and button, or a problem. */
export const refusalHasBody = (refusal: RefusedOutput): boolean => isFence(refusal) || refusal.problems.length > 0;

/**
 * The refusals that are about who the person is, not about what they said:
 * the old capability fences, and the roles route's own (roles-parity phase 5),
 * which the phone and the web draw as one fence with Ask an admin.
 */
const FENCE_CODES: ReadonlySet<string> = new Set([
  'NOT_ALLOWED',
  'LEVEL_ABOVE_YOURS',
  'INVITE_OWNER_REFUSED',
  'NOT_YOUR_RIGHT',
  'ABOVE_YOUR_ROLE',
  'PRIMARY_ONLY',
  'ADMIN_OPENS_ALL',
  'OWN_ACCESS',
  'OWN_ROLE',
  'FORM_NEEDS',
]);

export const isFence = (refusal: RefusedOutput): boolean => FENCE_CODES.has(refusal.code);

/**
 * What a fence's Ask an admin asks for: the record at a level, or the
 * permission, when the refusal names one (`right`), read defensively since a
 * tool output is JSON the model shaped. Null, the card opens Team instead.
 */
export const fenceAskOf = (refusal: RefusedOutput): AskCreateRequest['right'] | null => {
  if (!isFence(refusal)) return null;
  const right: unknown = refusal.right;
  if (typeof right !== 'object' || right === null) return null;
  const { recordId, access, permission } = right as { recordId?: unknown; access?: unknown; permission?: unknown };
  if (typeof permission === 'string' && (PERMISSION_KEYS as readonly string[]).includes(permission)) {
    return { permission: permission as PermissionKey };
  }
  if (typeof recordId === 'string' && recordId !== '' && (access === 'see' || access === 'change')) return { recordId, access };
  return null;
};

/** The record a proposal is about, or null for one about the business or its people. */
export const recordIdOf = (proposal: WideAgentProposal): string | null => {
  switch (proposal.type) {
    case 'entry':
    case 'entries':
    case 'entry_change':
    case 'structure':
    case 'card':
    case 'link_change':
    case 'grant':
    case 'kit':
      return proposal.recordId;
    case 'card_remove':
    case 'record_remove':
    case 'field_convert':
      return proposal.recordId;
    case 'link':
      return proposal.fromRecordId;
    case 'invite':
    case 'invites':
    case 'bundle':
    case 'setup':
    case 'record':
    case 'job':
    case 'job_change':
    case 'job_apply':
    case 'business':
    case 'member_change':
    case 'primary_request':
    case 'role':
    case 'role_change':
    case 'role_give':
    case 'access':
    case 'handover':
    case 'handover_answer':
    case 'invite_change':
    case 'ask_answer':
    case 'ask':
    case 'role_copy':
    case 'role_remove':
    case 'person_change':
      return null;
    case 'fact':
      // Filed under a record when the tool resolved one; a fact about a row
      // or a member, or about the business as a whole, is about no record.
      return proposal.about?.recordId ?? null;
  }
};

/**
 * A batch card minus the rows the person took off it, by their place in the
 * card as it arrived.
 *
 * The tap has to write exactly what the card shows, so the dropped rows are
 * taken out before the confirm, the title and the receipt count, all of which
 * read the proposal. Dropping every row leaves the card whole: there would be
 * nothing to confirm, and the body never offers the control on the last row.
 * Anything that is not a batch is handed back untouched.
 */
export const keptEntries = <P extends WideAgentProposal>(proposal: P, dropped: ReadonlySet<number>): P => {
  if (proposal.type !== 'entries' || dropped.size === 0) return proposal;
  const entries = proposal.entries.filter((_, index) => !dropped.has(index));
  return entries.length === 0 ? proposal : ({ ...proposal, entries } as P);
};

/**
 * A bundle part with the ids of the jobs and roles the parts before it made.
 *
 * `propose_bundle` may carry an invitation that names a job or a role the
 * same card creates. That one did not exist when the card was drawn, so the
 * tool left its id null and put its NAME on the invitation (`jobName` on the
 * old invitation, `person.roleName` on the new one); the tap runs the parts in
 * order, and by the time the invitation is sent the part before it has handed
 * back its id. `madeIds` is keyed by the name, folded, the way
 * role_name_unique folds it. An invitation that already carries an id, or
 * names one this card did not make, is handed back untouched.
 *
 * A link may lean on a record the same card creates (Decided L2 of
 * docs/plans/links-fixes.md): `madeRecords`, keyed by the record's name
 * folded, puts the made record's id where its placeholder stood, so the
 * link's part loads and posts on the right record. Its fields' ids are filled
 * at the tap itself, from the made record's schema (`linkReadyToPost`).
 */
export const withMadeIds = <P extends WideAgentProposal>(
  part: P,
  madeIds: ReadonlyMap<string, string>,
  madeRecords: ReadonlyMap<string, string> = new Map(),
): P => {
  if (part.type === 'link') {
    if (part.pendingRecord === undefined || !isPendingLink(part)) return part;
    const recordId = madeRecords.get(part.pendingRecord.name.trim().toLowerCase());
    return recordId === undefined ? part : (withPendingRecordId(part, recordId) as P);
  }
  const idOf = (name: string | null): string | null => (name === null ? null : (madeIds.get(name.trim().toLowerCase()) ?? null));
  const filled = (invite: InviteProposal): InviteProposal => {
    const person = invite.person;
    if (person !== undefined) {
      if (person.request.roleId !== null) return invite;
      const roleId = idOf(person.roleName);
      return roleId === null ? invite : { ...invite, person: { ...person, request: { ...person.request, roleId } } };
    }
    if (invite.roleId !== null) return invite;
    const roleId = idOf(invite.jobName);
    return roleId === null ? invite : { ...invite, roleId };
  };
  if (part.type === 'invite') return filled(part) as P;
  if (part.type === 'invites') return { ...part, invites: part.invites.map(filled) } as P;
  return part;
};

/**
 * A link ready to post: as it came, or — when it leans on a record the same
 * card made — with every placeholder swapped for the made record's ids, read
 * off that record's schema. A link whose record was not made, or whose record
 * lacks a field the link fills, is never posted on a guess: the words say
 * which, and the card stops there with the record standing.
 */
export const linkReadyToPost = async (
  proposal: LinkProposal,
  loadSchema: (recordId: string) => Promise<RecordSchemaResponse>,
): Promise<{ readonly ok: true; readonly proposal: LinkProposal } | { readonly ok: false; readonly message: string }> => {
  const pending = proposal.pendingRecord;
  if (pending === undefined) return { ok: true, proposal };
  if (isPendingLink(proposal)) return { ok: false, message: t('agent.link_record_not_made', { record: pending.name }) };
  const schema = await loadSchema(pending.side === 'from' ? proposal.fromRecordId : proposal.toRecordId);
  const filled = fillPendingLink(proposal, schema);
  if (!filled.ok) return { ok: false, message: t('agent.link_record_lacks', { record: pending.name, fields: filled.missing.join(', ') }) };
  return { ok: true, proposal: filled.proposal };
};

/** The name the web's bundle card still calls it by; the two join once the web draws roles. */
export const withJobIds = withMadeIds;

/* ------------------------------------------------------------------ cards */

/** The card and spec one tile turns out to be, when this build can draw it. */
type ParsedTile = NonNullable<ReturnType<typeof parseSummaryTile>>;

/**
 * The proposal's tile, read by the reader every stored tile goes through.
 *
 * The proposal carries the stored tile's own vocabulary with one difference: a
 * combined total rides under `combine` rather than inside `measure`, because
 * the tool that wrote it names a tree and the tile names an aggregate. It is
 * folded back here, which is the one place that translation belongs — the
 * device's grammar has always carried a combine as `{ agg: 'formula', expr }`.
 *
 * `measure`, `combine`, `group_via`, `leader_via` and a condition's `via` are
 * all handed straight to `parseSummaryTile`, which reads each of them and
 * drops the tile whole when it cannot.
 */
const parsedTile = (proposal: CardProposal, measureFieldId: string | null): ParsedTile | null => {
  const { tile } = proposal;
  // A tile that says what it counts twice is not one to guess at. The server
  // refuses that pair (`MEASURE_TWICE`), so meeting it here means the proposal
  // came from a build saying something this one cannot read — and drawing the
  // half it understands is a number under the wrong name.
  if (tile.measure !== undefined && tile.combine !== undefined) return null;
  const measure = tile.combine === undefined ? tile.measure : { agg: 'formula', expr: tile.combine };
  return parseSummaryTile(
    {
      card: proposal.card,
      label: tile.label,
      expr: tile.expr,
      measure,
      scope: tile.scope,
      group_by: tile.group_by,
      group_via: tile.group_via,
      leader_via: tile.leader_via,
      date_field: tile.date_field,
      days: tile.days,
      limit: tile.limit,
      where: tile.where,
    },
    measureFieldId,
  );
};

/**
 * The proposed card as a layout entry on the record's own summary, or null
 * when the tile is not one this build draws. The label is a literal word the
 * person chose, so it rides as `title` exactly as a composed template's
 * literal label does.
 */
export const cardLayoutEntry = (
  proposal: CardProposal,
  measureFieldId: string | null,
): InsightLayoutEntry | null => {
  const parsed = parsedTile(proposal, measureFieldId);
  if (parsed === null) return null;
  // The span and the size are both left to the card's own default, with the
  // one exception `defaultLayoutFor` makes: a leader answers a name, and a
  // name does not fit one cell.
  return layoutEntry(
    parsed.card,
    parsed.spec,
    undefined,
    isLeaderSpec(parsed.spec) ? 'wide' : undefined,
    null,
    proposal.tile.label,
  );
};

/**
 * The same card as the Dashboard hosts it: the record it is asked of, and that
 * record's own spec.
 *
 * `placement: 'dashboard'` is what a card whose totals reach a second record
 * carries, because a record's own summary can only answer for that record.
 * The entry is built by the BUSINESS layout's own builder, so its id is the
 * business hash of the same normalised spec — placing one question twice is
 * still one card, on the strip as on a record.
 *
 * `measureFieldId` is the HOST record's measure binding, and it defaults to
 * none: a hosted card is a combine, which names its fields itself and has no
 * `agg:field` string for a binding to shorten.
 */
export const hostedCardEntry = (
  proposal: CardProposal,
  measureFieldId: string | null = null,
): BusinessInsightLayoutEntry | null => {
  // A pinned money figure (step 12.5 of docs/plans/ledger.md): one number the
  // server answers, on an app that draws it. The phone never does, and never
  // is sent one (it does not say `ledger_cards`); were it, nothing is added.
  if (proposal.tile.ledger !== undefined) {
    const ledger = readLedgerCardSpec(proposal.tile.ledger);
    if (ledger === null || !drawsLedgerTiles()) return null;
    return businessEntry('insight.stat', ledger, undefined, null, proposal.tile.label);
  }
  const parsed = parsedTile(proposal, measureFieldId);
  if (parsed === null) return null;
  return businessEntry(
    parsed.card,
    { host: proposal.recordId, record: parsed.spec },
    undefined,
    null,
    proposal.tile.label,
  );
};

/* -------------------------------------------------------------- structure */

/** What the draft store's `addField` takes. */
export type DraftAddInput = Omit<
  DraftField,
  'key' | 'isNew' | 'removed' | 'optionsTouched' | 'boundPurposes' | 'tier' | 'storage'
>;

export type StructureEdit =
  /** `key` is the add's own key in the request, which a count elsewhere in it may name. */
  | { readonly kind: 'add'; readonly key: string; readonly field: DraftAddInput }
  | { readonly kind: 'update'; readonly patch: StructureFieldPatch }
  | { readonly kind: 'archive'; readonly id: string }
  /** Where each field shows from now on: the layout tree, or null to take it off. */
  | { readonly kind: 'layout'; readonly layout: RecordLayout | null }
  /** Every field in form order. A key may be an add's own key, as on the request. */
  | { readonly kind: 'order'; readonly order: readonly string[] }
  /** The field the form opens on, or null for none. May be an add's own key. */
  | { readonly kind: 'focus'; readonly key: string | null }
  /** The record's glyph, or null for its default. */
  | { readonly kind: 'icon'; readonly icon: RecordIconId | null }
  /** The record's new name. */
  | { readonly kind: 'name'; readonly name: string };

const draftOptions = (options: StructureFieldPatch['options']): DraftOption[] | null =>
  options === undefined
    ? null
    : options.map((option) => ({
        ...(option.id === undefined ? {} : { id: option.id }),
        label: option.label,
        sign: option.sign ?? null,
      }));

/** One added field, as the customize screen would have built it by hand. */
export const draftAddFrom = (add: StructureFieldAdd): DraftAddInput => ({
  label: add.label,
  type: add.type,
  enabled: true,
  required: add.required ?? false,
  sticky: add.sticky ?? false,
  helpText: add.helpText ?? null,
  // No component named means the type's primitive, the one every device draws.
  component: add.component ?? VALUE_TYPES[add.type].primitive,
  props: add.props ?? {},
  options: draftOptions(add.options),
  unitList: add.unitList ?? null,
  targetRecordId: add.targetRecordId ?? null,
  spec: add.spec ?? null,
  // What the field starts with. A default and a recipe are the same tree with
  // different owners, so both ride the same way: absent means nothing to fill.
  startsWith: add.startsWith ?? null,
  // When it shows, on the same terms.
  showWhen: add.showWhen ?? null,
  // The fields of each line of a new lines field, as draft fields of their
  // own. They keep the request's keys, which a line count inside them names.
  lines:
    add.lines === undefined
      ? null
      : { fields: add.lines.fields.map(lineDraftFrom), countFrom: add.lines.countFrom ?? null, max: add.lines.max ?? null },
});

/** One field of a line, as the draft holds the line fields of a new lines field. */
const lineDraftFrom = (add: StructureFieldAdd): DraftField => {
  const isLines = add.type === 'lines';
  const outsideSlots = VALUE_TYPES[add.type].home === 'attrs';
  return {
    ...draftAddFrom(add),
    key: add.key,
    isNew: true,
    tier: 'custom',
    storage: isLines ? 'children' : outsideSlots ? 'attr' : 'slot',
    boundPurposes: [],
    removed: false,
    optionsTouched: true,
  };
};

/**
 * The PATCH body as the edits the draft store takes, in the order the body
 * lists them.
 *
 * A recipe rides on both halves: `spec` on an add is carried by `draftAddFrom`
 * into the new field, and `spec` on an update is carried by the patch itself
 * and set through the store's own `setSpec`. What a field STARTS with rides
 * beside it, on both halves, through `setStartsWith`.
 *
 * The record's own changes come after the fields', because an order and a
 * focus may name a field added above them. A rename and an icon ride too: a
 * card that only renames the record is a card, and dropping them here once
 * left "Change it first" opening a draft with nothing in it. `capture` does
 * not: the draft holds no sheet setting, so a card that changes one is only
 * kept by its own tap, which sends the request as it was planned.
 */
export const structureEdits = (request: RecordStructureRequest): StructureEdit[] => [
  ...(request.add ?? []).map((add): StructureEdit => ({ kind: 'add', key: add.key, field: draftAddFrom(add) })),
  ...(request.update ?? []).map((patch): StructureEdit => ({ kind: 'update', patch })),
  ...(request.archive ?? []).map((id): StructureEdit => ({ kind: 'archive', id })),
  ...(request.order === undefined ? [] : [{ kind: 'order', order: request.order } satisfies StructureEdit]),
  ...(request.focusedInput === undefined ? [] : [{ kind: 'focus', key: request.focusedInput } satisfies StructureEdit]),
  ...(request.layout === undefined ? [] : [{ kind: 'layout', layout: request.layout } satisfies StructureEdit]),
  ...(request.icon === undefined ? [] : [{ kind: 'icon', icon: request.icon } satisfies StructureEdit]),
  ...(request.name === undefined ? [] : [{ kind: 'name', name: request.name } satisfies StructureEdit]),
];

/** What sending a structure card's request came to. */
export type AgentStructureSaved =
  | { readonly kind: 'saved' }
  | { readonly kind: 'stale' }
  | { readonly kind: 'refused'; readonly message: string }
  | { readonly kind: 'failed'; readonly message: string };

/** A request that names nothing but the version it was planned on. */
export const isEmptyStructureRequest = (request: RecordStructureRequest): boolean =>
  Object.keys(request).every((key) => key === 'fieldSetVer');

/**
 * Keep a structure card: send its request exactly as the server planned it,
 * then pull, so every list that shows the record's name and glyph learns the
 * change.
 *
 * Not through the customize draft. The draft carries what a person can edit
 * there, and a card can carry more (a sheet setting); a change the draft could
 * not hold used to come out of it as no change at all, and the card said Done
 * over a save that never left the device. A request with nothing in it is
 * refused here for the same reason: Done means the server holds the change.
 *
 * `patch` sends the request and lands the answer (the schema mirror, the
 * record's counter, the draft). `sync` pulls; it runs after any send, whether
 * or not it landed, as a new record's structure does.
 */
export const saveAgentStructure = async (
  request: RecordStructureRequest,
  deps: {
    readonly patch: (request: RecordStructureRequest) => Promise<AgentStructureSaved>;
    readonly sync: () => void;
  },
): Promise<AgentStructureSaved> => {
  if (isEmptyStructureRequest(request)) return { kind: 'failed', message: t('agent.structure_nothing') };
  try {
    return await deps.patch(request);
  } finally {
    deps.sync();
  }
};

/** The recipe one field spec holds, read back rather than cast. */
const formulaOf = (spec: FieldSpec | null | undefined): FormulaSpec | null =>
  spec === null || spec === undefined ? null : readFormulaSpec((spec as { formula?: unknown }).formula);

/**
 * One tree per line of a structure proposal's summary, in the summary's own
 * order, with null where a line carries none.
 *
 * The summary and the request are the same changes twice: the tool pushes one
 * summary line per add, then one per update, then one per archive, and pushes
 * nothing for a change it refused. So the nth line of a kind is the nth entry
 * of that kind. Matching on the label instead would pick the wrong field the
 * moment two of them share a word, and there is no id to match on — a summary
 * line carries a label and a change, and nothing else.
 *
 * Both `structureRecipes` and `structureDefaults` walk it, because a field may
 * carry a recipe and a default, and one rule for finding them is one rule to
 * keep right.
 */
const treesBy = (
  proposal: StructureProposal,
  of: (add: StructureFieldAdd | undefined, patch: StructureFieldPatch | undefined) => FormulaSpec | null,
): readonly (FormulaSpec | null)[] => {
  const seen: Record<StructureProposal['summary'][number]['change'], number> = {
    add: 0,
    update: 0,
    archive: 0,
    rename: 0,
    order: 0,
  };
  return proposal.summary.map((line) => {
    const at = seen[line.change];
    seen[line.change] = at + 1;
    if (line.change === 'add') return of(proposal.request.add?.[at], undefined);
    if (line.change === 'update') return of(undefined, proposal.request.update?.[at]);
    return null;
  });
};

/**
 * What each line of the summary STARTS with, in the summary's own order, by
 * the same positional rule the recipes are matched by.
 *
 * A cleared default (`null` on a patch) reads as no default here, which is
 * what the card should say: the field is about to hold nothing to start with,
 * and there is no sentence for nothing.
 */
export const structureDefaults = (proposal: StructureProposal): readonly (FormulaSpec | null)[] =>
  treesBy(proposal, (add, patch) => readFormulaSpec(add?.startsWith ?? patch?.startsWith));

/** The recipe behind each line, with null where a line is not a worked-out one. */
export const structureRecipes = (proposal: StructureProposal): readonly (FormulaSpec | null)[] =>
  treesBy(proposal, (add, patch) => formulaOf(add?.spec ?? patch?.spec));

/**
 * The words a card's recipes are said in: the record's own, with the labels
 * the change gives its fields, and each field it adds by the handle a recipe
 * reads it by. Without this a recipe that reads the switch added beside it is
 * said with the handle, "new_1", where the person wrote "Apply 15% GST".
 */
export const structureWords = (request: RecordStructureRequest, words: FormulaWordLookup): FormulaWordLookup => {
  const named = new Map<string, string>();
  for (const add of request.add ?? []) if (add.key !== undefined && add.key !== '') named.set(add.key, add.label);
  for (const patch of request.update ?? []) if (patch.label !== undefined) named.set(patch.id, patch.label);
  return { own: (fieldId) => named.get(fieldId) ?? words.own(fieldId), hop: words.hop };
};

/** The store actions the edits need. Typed off the store so the two cannot drift. */
export type DraftActions = Pick<
  ReturnType<typeof useStructureDraft.getState>,
  | 'addField'
  | 'setLabel'
  | 'setHelpText'
  | 'setEnabled'
  | 'setRequired'
  | 'setSticky'
  | 'setOptions'
  | 'setSpec'
  | 'setStartsWith'
  | 'setShowWhen'
  | 'setCountFrom'
  | 'setLayout'
  | 'setRemoved'
  | 'setOrder'
  | 'setFocused'
  | 'setIcon'
  | 'setName'
>;

/**
 * Apply the edits through the store's own setters, so every rule those setters
 * hold (a required flag a type cannot carry, a new field forgotten on remove)
 * applies to the agent's change exactly as it applies to a tap.
 */
export const applyStructureEdits = (draft: DraftActions, edits: readonly StructureEdit[]): void => {
  // The request names a new field by its own key, and the draft hands each
  // one a local key of its own. A recipe may read a field the same change
  // adds (the planner allows it since 5 Oct 2026), and a lines field counted
  // by a number field added beside it names that field's request key. So
  // every add goes in first, and a recipe or a count that names a request key
  // is put back on the draft's key, or it would read a field nothing holds.
  const keyOf = new Map<string, string>();
  const counted: Array<{ readonly linesKey: string; readonly countFrom: string }> = [];
  const added: { readonly key: string; readonly spec: FieldSpec }[] = [];
  for (const edit of edits) {
    if (edit.kind !== 'add') continue;
    const made = draft.addField(edit.field);
    keyOf.set(edit.key, made);
    const countFrom = edit.field.lines?.countFrom ?? null;
    if (countFrom !== null) counted.push({ linesKey: made, countFrom });
    if (edit.field.spec !== null) added.push({ key: made, spec: edit.field.spec });
  }
  const readsAnAdd = (spec: FieldSpec): FormulaSpec | null => {
    const formula = readFormulaSpec((spec as { formula?: unknown }).formula);
    return formula !== null && formulaFieldsRead(formula).some((fieldId) => keyOf.has(fieldId)) ? formula : null;
  };
  const rekeyed = (spec: FieldSpec): FieldSpec => {
    const formula = readsAnAdd(spec);
    return formula === null ? spec : { ...spec, formula: formulaWithFieldIds(formula, keyOf) };
  };
  for (const one of added) if (readsAnAdd(one.spec) !== null) draft.setSpec(one.key, rekeyed(one.spec));
  for (const edit of edits) {
    if (edit.kind === 'add') continue;
    if (edit.kind === 'archive') {
      draft.setRemoved(edit.id, true);
      continue;
    }
    if (edit.kind === 'layout') {
      draft.setLayout(edit.layout);
      continue;
    }
    if (edit.kind === 'order') {
      draft.setOrder(edit.order.map((key) => keyOf.get(key) ?? key));
      continue;
    }
    if (edit.kind === 'focus') {
      draft.setFocused(edit.key === null ? null : (keyOf.get(edit.key) ?? edit.key));
      continue;
    }
    if (edit.kind === 'icon') {
      draft.setIcon(edit.icon);
      continue;
    }
    if (edit.kind === 'name') {
      draft.setName(edit.name);
      continue;
    }
    const { patch } = edit;
    if (patch.label !== undefined) draft.setLabel(patch.id, patch.label);
    if (patch.helpText !== undefined) draft.setHelpText(patch.id, patch.helpText ?? '');
    if (patch.enabled !== undefined) draft.setEnabled(patch.id, patch.enabled);
    if (patch.required !== undefined) draft.setRequired(patch.id, patch.required);
    if (patch.sticky !== undefined) draft.setSticky(patch.id, patch.sticky);
    if (patch.options !== undefined) draft.setOptions(patch.id, draftOptions(patch.options) ?? []);
    // A recipe is its own edit, not part of the rename beside it, so it goes
    // through the store's own setter — the same one the builder's Save calls.
    // A null spec would be "take the recipe away", which no screen offers and
    // the store cannot say; the tool never sends one.
    if (patch.spec !== undefined && patch.spec !== null) draft.setSpec(patch.id, rekeyed(patch.spec));
    // A default is its own edit too, and unlike a recipe it CAN be taken away:
    // null is the gesture for "it starts with nothing from now on", and the
    // store and the save both carry that null through to the server.
    if (patch.startsWith !== undefined) draft.setStartsWith(patch.id, patch.startsWith);
    if (patch.showWhen !== undefined) draft.setShowWhen(patch.id, patch.showWhen);
    // A lines field's count, which may name a number field added beside it.
    const lines = patch.spec === undefined || patch.spec === null ? null : linesOf(patch.spec);
    if (lines !== null && lines.countFrom !== null) counted.push({ linesKey: patch.id, countFrom: lines.countFrom });
  }
  for (const { linesKey, countFrom } of counted) {
    const named = keyOf.get(countFrom);
    if (named !== undefined) draft.setCountFrom(linesKey, named);
  }
};

/* ------------------------------------------------------------------ links */

/**
 * The proposed link as the draft the link editor holds.
 *
 * The trigger and the map are carried across whole rather than rebuilt, and
 * that is what makes a worked-out mapping and a hop condition work here for
 * free: the draft holds the request's own shapes, so a `{kind:'formula'}`
 * answer and a condition's `via` reach the editor exactly as the tool wrote
 * them. Anything this file re-typed would be a second grammar to keep in step.
 */
export const linkDraftFrom = (proposal: LinkProposal): LinkDraft => ({
  editing: null,
  toRecordId: proposal.toRecordId,
  trigger: proposal.request.trigger ?? { all: [] },
  fieldMap: proposal.request.fieldMap,
  sign: proposal.request.sign ?? 1,
  mode: proposal.request.mode ?? 'auto',
});

/** A stored link's draft with the proposed change laid over it. Only what the patch names moves. */
export const linkDraftPatched = (draft: LinkDraft, patch: LinkPatchRequest): LinkDraft => ({
  ...draft,
  ...(patch.trigger === undefined ? {} : { trigger: patch.trigger }),
  ...(patch.fieldMap === undefined ? {} : { fieldMap: patch.fieldMap }),
  ...(patch.sign === undefined ? {} : { sign: patch.sign }),
  ...(patch.mode === undefined ? {} : { mode: patch.mode }),
});

/** What the agent said follows once this proposal is confirmed, or null. Drawn as "Coming next" under a confirmed card. */
export const nextOf = (part: Part): string | null => {
  const output = landedOutput(part);
  if (output === null || output.kind !== 'proposal') return null;
  return typeof output.next === 'string' && output.next.trim() !== '' ? output.next : null;
};

/**
 * What the person should hear beside a proposal, or nothing. A tool writes
 * these where it chose for the person: a Remember card offered as a `person`
 * fact to somebody who may not write for the business says here who can make
 * it the business's (decision 14 of docs/plans/agent-business-memory.md). The
 * setup door reads the same member off its own envelope.
 */
export const notesOf = (part: Part): readonly string[] => {
  const output = landedOutput(part);
  if (output === null || output.kind !== 'proposal' || !Array.isArray(output.notes)) return [];
  return output.notes.filter((note): note is string => typeof note === 'string' && note.trim() !== '');
};
